import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BookOpen,
  Check,
  CircleAlert,
  CloudUpload,
  History,
  Loader2,
  Printer,
  Redo2,
  Save,
  Undo2,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProjeto } from '@/contexts/projeto-hook';
import { DocumentoCompleto } from './documento/pagina-documento';
import { MedidorBlocos } from './documento/medidor';
import { PainelPrevia } from './documento/painel-previa';
import { montarBlocos, paginarDocumento } from './documento/montar-documento';
import { PainelVersoes } from './componentes/painel-versoes';
import { ContextoManual } from './contexto-manual';
import { STATUS_MANUAL, criarManual, type StatusManual } from './manual-api';
import { SECOES } from './manual-defaults';
import { SecaoDocumento } from './secoes/secao-documento';
import { SecaoEncerramento } from './secoes/secao-encerramento';
import { SecaoIdentificacao } from './secoes/secao-identificacao';
import { SecaoInstalacao } from './secoes/secao-instalacao';
import { SecaoManutencao } from './secoes/secao-manutencao';
import { SecaoOperacao } from './secoes/secao-operacao';
import { SecaoSeguranca } from './secoes/secao-seguranca';
import { SecaoVisaoGeral } from './secoes/secao-visao-geral';
import { normalizarNomeArquivo } from './manual-utils';
import { useManualEditor } from './use-manual-editor';
import type { SecaoId } from './manual-types';
import './estilos/manual-workspace.css';
import './estilos/manual-documento.css';

export function ManualOperacaoPage() {
  const navigate = useNavigate();
  const [parametros, definirParametros] = useSearchParams();
  const manualId = parametros.get('id');
  const { projeto } = useProjeto();
  const editor = useManualEditor({ projetoId: projeto?.id, manualId });
  const [secaoAtiva, setSecaoAtiva] = useState<SecaoId>('01');
  const [imprimindo, setImprimindo] = useState(false);
  const [versoesAbertas, setVersoesAbertas] = useState(false);
  const [publicando, setPublicando] = useState(false);

  const {
    dados, manual, avaliacao, estado, salvoEm, carregando, erro,
    salvar, desfazer, refazer, podeDesfazer, podeRefazer,
    definirStatus, registrarPaginas,
  } = editor;

  const [alturas, setAlturas] = useState<Record<string, number>>({});
  const blocos = useMemo(() => montarBlocos(dados), [dados]);
  const documento = useMemo(() => paginarDocumento(blocos, dados, alturas), [blocos, dados, alturas]);

  // A capa não entra na paginação do miolo, mas conta como folha do PDF.
  useEffect(() => {
    registrarPaginas(documento.totalPaginas + 1);
  }, [documento.totalPaginas, registrarPaginas]);

  const imprimir = useCallback(() => setImprimindo(true), []);

  useEffect(() => {
    if (!imprimindo) return;
    const encerrar = () => setImprimindo(false);
    window.addEventListener('afterprint', encerrar);
    // O navegador usa o título da página como nome do PDF salvo.
    const tituloOriginal = document.title;
    const nome = normalizarNomeArquivo(dados.nomeMaquina || manual?.codigo || 'manual-operacao');
    document.title = `manual-${nome}-rev-${String(manual?.revisao ?? 0).padStart(2, '0')}`;
    // dá um quadro para o documento completo entrar no DOM antes da caixa de impressão
    const id = window.setTimeout(() => window.print(), 150);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('afterprint', encerrar);
      document.title = tituloOriginal;
    };
  }, [dados.nomeMaquina, imprimindo, manual?.codigo, manual?.revisao]);

  useEffect(() => {
    const atalho = (evento: KeyboardEvent) => {
      if (!(evento.ctrlKey || evento.metaKey)) return;
      const tecla = evento.key.toLowerCase();
      if (tecla === 's') {
        evento.preventDefault();
        void salvar();
      } else if (tecla === 'p') {
        evento.preventDefault();
        imprimir();
      } else if (tecla === 'z' && !evento.shiftKey) {
        evento.preventDefault();
        desfazer();
      } else if ((tecla === 'z' && evento.shiftKey) || tecla === 'y') {
        evento.preventDefault();
        refazer();
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, [desfazer, imprimir, refazer, salvar]);

  /** Rascunho local vira manual do histórico, sem perder o que já foi digitado. */
  const publicarNoHistorico = async () => {
    if (!projeto?.id) {
      window.alert('Selecione um projeto antes de salvar o manual no histórico.');
      return;
    }
    setPublicando(true);
    try {
      const novo = await criarManual({
        projetoId: String(projeto.id),
        dados,
        metricas: {
          progresso: avaliacao.geral,
          pendenciasCriticas: avaliacao.pendenciasCriticas,
          totalPaginas: documento.totalPaginas + 1,
        },
      });
      definirParametros({ id: novo.id }, { replace: true });
    } catch (falha) {
      window.alert(falha instanceof Error ? falha.message : 'Não foi possível salvar o manual no histórico.');
    } finally {
      setPublicando(false);
    }
  };

  const secaoAtual = SECOES.find((secao) => secao.id === secaoAtiva) ?? SECOES[0];

  const formulario = (() => {
    switch (secaoAtiva) {
      case '01': return <SecaoIdentificacao editor={editor} />;
      case '02': return <SecaoSeguranca editor={editor} />;
      case '03': return <SecaoVisaoGeral editor={editor} />;
      case '04': return <SecaoInstalacao editor={editor} />;
      case '05': return <SecaoOperacao editor={editor} />;
      case '06': return <SecaoManutencao editor={editor} />;
      case '07': return <SecaoEncerramento editor={editor} />;
      default: return <SecaoDocumento editor={editor} documento={documento} aoIrParaSecao={setSecaoAtiva} />;
    }
  })();

  const rotuloSalvamento = estado === 'erro'
    ? 'Falha ao salvar'
    : estado === 'pendente'
      ? 'Salvando…'
      : salvoEm
        ? `Salvo ${salvoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
        : manualId ? 'Salvar' : 'Salvar rascunho';

  return (
    <ContextoManual.Provider value={{ manualId }}>
      <div className="manual-workspace">
        <header className="manual-app-header">
          <div className="manual-app-brand">
            <button
              type="button"
              onClick={() => navigate(manualId ? '/manuais' : '/projeto-selecao')}
              title={manualId ? 'Voltar ao histórico de manuais' : 'Voltar aos projetos'}
            >
              <ArrowLeft />
            </button>
            <span><BookOpen /></span>
            <div>
              <strong>{manual ? `${manual.codigo} · Rev. ${String(manual.revisao).padStart(2, '0')}` : 'Manual de Operação'}</strong>
              <small>{dados.nomeMaquina || manual?.titulo || projeto?.nome || 'Novo manual'}</small>
            </div>
          </div>

          <div className="manual-app-progress">
            <span>
              {avaliacao.geral}% preenchido
              {avaliacao.pendenciasCriticas > 0 && ` · ${avaliacao.pendenciasCriticas} pendência${avaliacao.pendenciasCriticas > 1 ? 's' : ''} crítica${avaliacao.pendenciasCriticas > 1 ? 's' : ''}`}
            </span>
            <div><i style={{ width: `${avaliacao.geral}%` }} /></div>
          </div>

          <div className="manual-app-actions">
            {manual && (
              <select
                className="manual-status-select"
                value={manual.status}
                title="Status do manual"
                onChange={(evento) => void definirStatus(evento.target.value as StatusManual)}
              >
                {STATUS_MANUAL.map((item) => (
                  <option key={item.valor} value={item.valor}>{item.rotulo}</option>
                ))}
              </select>
            )}
            <button type="button" className="manual-icon-button" title="Desfazer (Ctrl+Z)" disabled={!podeDesfazer} onClick={desfazer}><Undo2 /></button>
            <button type="button" className="manual-icon-button" title="Refazer (Ctrl+Shift+Z)" disabled={!podeRefazer} onClick={refazer}><Redo2 /></button>
            {manualId ? (
              <button
                type="button"
                className={`manual-secondary-button ${versoesAbertas ? 'is-ativo' : ''}`}
                onClick={() => setVersoesAbertas((aberto) => !aberto)}
                title="Histórico de versões"
              >
                <History />Versões{manual?._count?.versoes ? ` (${manual._count.versoes})` : ''}
              </button>
            ) : (
              <button
                type="button"
                className="manual-secondary-button"
                onClick={() => void publicarNoHistorico()}
                disabled={publicando}
                title="Guardar este rascunho no histórico do projeto"
              >
                {publicando ? <Loader2 className="gira" /> : <CloudUpload />}
                Salvar no histórico
              </button>
            )}
            <button type="button" className={`manual-secondary-button ${estado === 'erro' ? 'is-erro' : ''}`} onClick={() => void salvar()} title="Ctrl+S">
              {estado === 'erro' ? <CircleAlert /> : estado === 'salvo' ? <Check /> : <Save />}
              {rotuloSalvamento}
            </button>
            <button type="button" className="manual-primary-button" onClick={imprimir} title="Ctrl+P"><Printer />Gerar PDF</button>
          </div>
        </header>

        {(erro || (!manualId && !carregando)) && (
          <div className={`manual-aviso ${erro ? 'is-erro' : ''}`}>
            {erro
              ? <><CircleAlert />{erro}</>
              : <><CloudUpload />Este manual ainda é um rascunho deste navegador. Use “Salvar no histórico” para versioná-lo junto com os demais manuais do projeto.</>}
          </div>
        )}

        <nav className="manual-step-nav" aria-label="Etapas do manual">
          {SECOES.map((secao) => {
            const resumo = avaliacao.secoes[secao.id];
            const criticas = resumo?.pendencias.filter((pendencia) => pendencia.critico).length ?? 0;
            return (
              <button
                key={secao.id}
                type="button"
                className={secaoAtiva === secao.id ? 'is-active' : ''}
                onClick={() => setSecaoAtiva(secao.id)}
                title={secao.descricao}
              >
                <span>{secao.id}</span>
                {secao.titulo}
                <i className={criticas > 0 ? 'is-critico' : resumo?.percentual === 100 ? 'is-ok' : ''}>{resumo?.percentual ?? 0}%</i>
              </button>
            );
          })}
        </nav>

        <main className={`manual-editor-shell ${versoesAbertas ? 'com-versoes' : ''}`}>
          <section className="manual-form-panel">
            <div className="manual-form-heading">
              <div>
                <span>ETAPA {secaoAtual.id}</span>
                <h1>{secaoAtual.titulo}</h1>
                <p>{secaoAtual.descricao}</p>
              </div>
            </div>
            {carregando ? (
              <p className="manual-versoes-vazio"><Loader2 className="gira" />Abrindo o manual…</p>
            ) : formulario}
          </section>

          <PainelPrevia dados={dados} documento={documento} aoImprimir={imprimir} />

          {versoesAbertas && manualId && (
            <PainelVersoes editor={editor} aoFechar={() => setVersoesAbertas(false)} />
          )}
        </main>

        <MedidorBlocos blocos={blocos} densidade={dados.opcoes.densidadeTexto} aoMedir={setAlturas} />

        {imprimindo && (
          <div className="manual-print-doc">
            <DocumentoCompleto dados={dados} documento={documento} />
          </div>
        )}
      </div>
    </ContextoManual.Provider>
  );
}

export default ManualOperacaoPage;
